import React, { useState, useRef } from 'react';
import { Upload, Download, X, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { aiCertificationAPI } from '../../services/api';

const AICertBulkImportModal = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.xlsx'))) {
      setFile(f);
      setResult(null);
    } else {
      alert('Please select a CSV or XLSX file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.xlsx'))) {
      setFile(f);
      setResult(null);
    } else {
      alert('Please select a CSV or XLSX file');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await aiCertificationAPI.downloadBulkTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ai_certification_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to download template'); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await aiCertificationAPI.bulkImport(formData);
      setResult(res.data);
      if (res.data.created > 0 && onSuccess) onSuccess();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ai-cert-bulk-import-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-bold text-gray-900">Bulk Import Investors</h3>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-bulk-import"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-blue-800">Download Template</p>
              <p className="text-xs text-blue-600">Use this template to prepare your data</p>
            </div>
            <button onClick={handleDownloadTemplate} className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700" data-testid="download-template-btn">
              <Download size={14} className="mr-1.5" /> Template
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Columns: Investor Name, PAN, Type, Resident (Yes/No), Address, City, State, Pin/Zip, Email, Mobile
          </p>

          {/* File drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx" onChange={handleFileSelect} className="hidden" data-testid="bulk-file-input" />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={20} className="text-blue-500" />
                <span className="text-sm font-medium text-gray-800">{file.name}</span>
                <button onClick={e => { e.stopPropagation(); setFile(null); setResult(null); }} className="text-gray-400 hover:text-red-500"><X size={16} /></button>
              </div>
            ) : (
              <>
                <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Drop a file here or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">CSV and XLSX supported</p>
              </>
            )}
          </div>

          {/* Result */}
          {result && (
            <div className={`p-3 rounded-lg text-sm ${result.error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`} data-testid="import-result">
              {result.error ? (
                <div className="flex items-start gap-2"><AlertCircle size={16} className="mt-0.5 flex-shrink-0" /><span>{result.error}</span></div>
              ) : (
                <div>
                  <div className="flex items-center gap-2"><CheckCircle size={16} /><span className="font-medium">{result.message}</span></div>
                  {result.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      {result.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={handleClose} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={handleUpload} disabled={!file || uploading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="import-btn">
            {uploading ? <><Loader size={14} className="mr-2 animate-spin" /> Importing...</> : <><Upload size={14} className="mr-2" /> Import</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AICertBulkImportModal;
